<template>
  <ShortcutWrapper :nShortcutRequiredClaim="UserClaims.ContractManager" @n-shortcut="mutate(null, 'create')">
    <v-container fluid>
      <v-row dense>
        <v-col cols="12">

          <Dialog
          :stateId="dialogId"
          maxWidth="400px"
          @click:outside="close"
          @dialog-closing="close"
          @close="close">
            <ConfirmDelete
            v-if="confirmDeleteDialog"
            @delete="performDelete"
            @cancel-delete="close"
            :title="$t('bidCostType')"/>
            <BidCostTypeForm
            v-else
            :propBidCostType="bidCostType"
            @bid-cost-type-mutated="refresh" />
          </Dialog>

          <v-row>
            <v-col style="max-width: 800px;">
              <DataTable
              :tableHeightCalculationData="{ offset: 590, absoluteMinimum: 128 }"
              :customCells="customColumns"
              :headers="headers"
              :items="allBidCostTypes"
              :loading="loading">

                <template #actions="{ item }">
                  <Icon
                  icon="mdi-pencil"
                  :tooltipText="$t('edit')"
                  @icon-clicked="mutate(item, 'edit')"
                  :disabled="!userAssignedClaim(UserClaims.ContractManager)"
                  elementId="bid-cost-type-edit"/>
                  <Icon icon="mdi-delete-forever"
                  :tooltipText="$t('delete')"
                  @icon-clicked="mutate(item, 'delete')"
                  :disabled="!userAssignedClaim(UserClaims.ContractManager)"
                  elementId="bid-cost-type-delete"/>
                </template>

                <template #custom-controls>
                  <Icon
                  icon="mdi-refresh"
                  :tooltipText="$t('refresh')"
                  @icon-clicked="refresh"
                  :small="false"/>
                  <Icon icon="mdi-plus"
                  :tooltipText="$t('newBidCostType')"
                  @icon-clicked="mutate(null, 'create')"
                  :small="false"
                  :disabled="!userAssignedClaim(UserClaims.ContractManager)"
                  elementId="bid-cost-type-create"/>
                  <Icon
                  v-if="allBidCostTypes.length > 0"
                  :icon="strictCosts ? 'mdi-lock' : 'mdi-lock-open-variant'"
                  :tooltipText="$t(strictCosts ? 'bidCostTypeModeStrict' : 'bidCostTypeModeMixed')"
                  :iconColor="strictCosts ? 'primary' : 'grey darken-2'"
                  @icon-clicked="toggleBidCostTypeMode"
                  :small="false"
                  :disabled="!userAssignedClaim(UserClaims.GlobalAdmin) || togglingMode"
                  elementId="bid-cost-type-mode"/>
                </template>

              </DataTable>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-container>
  </ShortcutWrapper>
</template>

<script>
import { cloneDeep } from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import { BidCostTypeMode, UserClaims } from '@/utils/Enumerations.js'
import BidCostTypeHeaders from '@/headers/BidCostType'
import { userAssignedClaim } from '@/utils/ClaimUtility'
import { uniqueDialogId } from '@/utils/componentHelpers'

export default {
  name: 'BidCostTypeSettings',

  components: {
    ConfirmDelete: () => import('@/components/helper/ConfirmDelete.vue'),
    ShortcutWrapper: () => import('@/components/core/ShortcutWrapper.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    DataTable: () => import('@/components/core/table/DataTable.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    BidCostTypeForm: () => import('@/components/settings-components/bid-settings/BidCostTypeForm.vue')
  },

  created () {
    this.refresh()
  },

  computed: {
    ...mapGetters('tract', ['allBidCostTypes']),
    ...mapGetters('user', ['companyInfo']),

    strictCosts () {
      return this.companyInfo?.bidCostTypeMode !== BidCostTypeMode.Mixed.value
    },

    headers () {
      return BidCostTypeHeaders.bidCostTypeHeaders()
    },

    customColumns () {
      return [{
        slotName: 'actions',
        value: 'actions'
      }]
    }
  },

  data: () => ({
    confirmDeleteDialog: false,
    loading: false,
    bidCostType: undefined,
    dialogId: uniqueDialogId('bid-cost-type-settings-dialog'),
    togglingMode: false,
    UserClaims
  }),

  methods: {
    ...mapActions('tract', ['fetchBidCostTypes', 'deleteBidCostType']),
    ...mapActions('user', ['patchCompanyInfo', 'fetchCompanyInfo']),
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),

    userAssignedClaim,

    refresh () {
      this.close()
      this.loading = true
      this.fetchBidCostTypes().finally(() => {
        this.loading = false
      })
    },

    mutate (bidCostType, mutationType) {
      this.bidCostType = cloneDeep(bidCostType)
      if (mutationType === 'delete') this.confirmDeleteDialog = true
      this.openOrUpdateDialog(this.dialogId)
    },

    close () {
      this.confirmDeleteDialog = false
      this.closeDialogsAtOrAbove(this.dialogId)
      this.bidCostType = undefined
    },

    async performDelete () {
      await this.deleteBidCostType(this.bidCostType.bidCostTypeId)
      this.refresh()
    },

    async toggleBidCostTypeMode () {
      const next = this.strictCosts ? BidCostTypeMode.Mixed : BidCostTypeMode.Strict
      this.togglingMode = true
      try {
        await this.patchCompanyInfo([{ op: 'add', path: '/bidCostTypeMode', value: next.value }])
        await this.fetchCompanyInfo()
        this.setSnack(this.$t('bidCostTypeModeUpdated', { mode: this.$t(next.name) }))
      } finally {
        this.togglingMode = false
      }
    }
  }
}
</script>
